import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { ArrowLeft } from 'lucide-react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@workspace/ui';
import AccountWorkspaceTabs from '@/components/account/AccountWorkspaceTabs';
import ActiveAccountPrompt from '@/components/account/ActiveAccountPrompt';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigation } from '@/context/NavigationContext';
import { setActiveAccount } from '@/lib/accounts';
import { buildAccountRoute, parseAccountRoute } from '@/lib/account-routing';
import {
  formatCurrency,
  formatDate,
  getLegacyDate,
  getLegacyNumber,
  getLegacyString,
  listMyAccountServices,
  type LegacyRecord
} from '@/lib/my-account';

export default function MyAccountServicesPage() {
  const { path, navigate } = useNavigation();
  const accountId = useMemo(() => parseAccountRoute(path).accountId, [path]);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    void setActiveAccount(accountId).catch(() => undefined);
  }, [accountId]);

  const {
    data: services = [],
    isLoading,
    error
  } = useSWR<LegacyRecord[]>(
    accountId ? ['/api/v1/my-account/services', accountId] : null,
    () => listMyAccountServices(accountId ?? undefined)
  );

  const [selectedService, setSelectedService] = useState<LegacyRecord | null>(null);

  if (!accountId) {
    return <ActiveAccountPrompt />;
  }

  const rows = useMemo(() =>
    services.map(service => {
      const description = getLegacyString(service, ['ServiceDescription', 'serviceDescription', 'description', 'Service']) ?? 'Service';
      const serviceId = getLegacyString(service, ['ServiceID', 'serviceId', 'serviceid']) ?? '—';
      const billingMethod = getLegacyString(service, ['BillingMethod', 'billingMethod']) ?? '';
      const billingFreq = getLegacyString(service, ['billingFreq', 'billingFrequency', 'frequency', 'billingfrequency']) ?? '';
      const term = [billingMethod, billingFreq].filter(Boolean).join(' · ') || '—';
      const location = getLegacyString(service, ['Location', 'location', 'serviceLocation', 'address', 'city']) ?? '—';
      const startDate = formatDate(getLegacyDate(service, ['StartingDate', 'startDate', 'start', 'started']));
      const expDate = formatDate(getLegacyDate(service, ['ExpirationDate', 'expDate', 'endDate', 'expiration']));
      const price = formatCurrency(getLegacyNumber(service, ['BillingPrice', 'billingPrice', 'price', 'amount']));

      return {
        raw: service,
        description,
        serviceId,
        term,
        location,
        startDate,
        expDate,
        price
      };
    }),
  [services]);

  const activeServiceId = getLegacyString(selectedService ?? {}, ['ServiceID', 'serviceId', 'serviceid']);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Button variant="ghost" type="button" onClick={() => navigate(buildAccountRoute(accountId))}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          Back to overview
        </Button>
        <AccountWorkspaceTabs accountId={accountId} current="services" />
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Services</h1>
          <p className="text-sm text-muted-foreground">Review active services and the latest billing details for this account.</p>
        </div>
      </div>

      {error ? (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Unable to load services</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Service list</CardTitle>
          <CardDescription>
            {isLoading ? 'Loading services…' : services.length ? 'Select a service to view detail.' : 'No services found.'}
          </CardDescription>
        </CardHeader>
        {isLoading ? (
          <CardContent className="px-6 py-10 text-center text-sm text-muted-foreground">Loading services…</CardContent>
        ) : services.length === 0 ? (
          <CardContent className="px-6 py-10 text-center text-sm text-muted-foreground">No services found.</CardContent>
        ) : (
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Service ID</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Exp Date</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow
                    key={`${row.serviceId}-${row.description}`}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setSelectedService(row.raw)}
                  >
                    <TableCell>
                      <div className="font-medium text-foreground">{row.description}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{row.serviceId}</TableCell>
                    <TableCell>{row.term}</TableCell>
                    <TableCell>{row.location}</TableCell>
                    <TableCell>{row.startDate}</TableCell>
                    <TableCell>{row.expDate}</TableCell>
                    <TableCell className="text-right">{row.price}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>

      {selectedService ? (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle>Service detail</CardTitle>
                <CardDescription>Additional information for the selected service.</CardDescription>
              </div>
              {activeServiceId ? <Badge variant="secondary">Service ID {activeServiceId}</Badge> : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <ServiceDetail label="Description" value={getLegacyString(selectedService, ['ServiceDescription', 'description', 'Service'])} />
            <ServiceDetail label="Username" value={getLegacyString(selectedService, ['ServiceUsername', 'username', 'serviceUsername'])} />
            <ServiceDetail label="Billing" value={getLegacyString(selectedService, ['BillingMethod', 'billingMethod'])} />
            <ServiceDetail label="Frequency" value={getLegacyString(selectedService, ['billingFreq', 'billingFrequency', 'frequency'])} />
            <ServiceDetail label="Start date" value={formatDate(getLegacyDate(selectedService, ['StartingDate', 'startDate', 'started']))} />
            <ServiceDetail label="Expiration date" value={formatDate(getLegacyDate(selectedService, ['ExpirationDate', 'expDate', 'expiration']))} />
            <ServiceDetail label="Price" value={formatCurrency(getLegacyNumber(selectedService, ['BillingPrice', 'billingPrice', 'price', 'amount']))} />
            <div className="pt-2">
              <Button type="button" variant="outline" onClick={() => setSelectedService(null)}>
                Clear selection
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function ServiceDetail({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs uppercase tracking-wide">{label}</span>
      <span className="text-sm text-foreground">{value && value !== '—' ? value : '—'}</span>
    </div>
  );
}
